import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Play, CheckCircle2, Ban, Save, Truck } from 'lucide-react';
import { toast } from 'sonner';

const blankRecipeRow = () => ({ raw_item_id: '', ratio_percent: '', wastage_percent: '', notes: '' });
const blankSessionRawRow = () => ({ raw_item_id: '', kg_required: '', notes: '' });
const blankBlendWastageRow = () => ({ raw_item_id: '', wastage_kg: '', notes: '' });

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function BlendOperationsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blendItems, setBlendItems] = useState([]);
  const [blendSessions, setBlendSessions] = useState([]);
  const [blendLots, setBlendLots] = useState([]);
  const [blendMovements, setBlendMovements] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedBlendItemId, setSelectedBlendItemId] = useState('');
  const [recipeForm, setRecipeForm] = useState({ notes: '', inputs: [blankRecipeRow()] });
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ blend_name: '', blend_code: '', notes: '', is_active: true });
  const [editingBlendItem, setEditingBlendItem] = useState(null);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    session_name: '',
    blend_item_id: '',
    source_warehouse_id: '',
    planned_output_kg: '',
    planned_raw_inputs: [blankSessionRawRow()],
    notes: '',
  });
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    actual_output_kg: '',
    production_loss_kg: '',
    notes: '',
    actual_raw_inputs: [blankSessionRawRow()],
    raw_wastage_inputs: [blankBlendWastageRow()],
  });

  const activeBlendItem = useMemo(
    () => blendItems.find((item) => item.id === selectedBlendItemId) || null,
    [blendItems, selectedBlendItemId]
  );

  const packingWarehouseId = useMemo(() => {
    const preferred = warehouses.find((warehouse) => warehouse.warehouse_type === 'PACKING' || warehouse.tag === 'PACKING');
    return preferred?.id || warehouses[0]?.id || '';
  }, [warehouses]);

  const getBlendSessionSteps = (session) => {
    const prepared = String(session.stock_preparation_status || 'not_prepared') === 'prepared';
    const completed = session.status === 'completed';
    const started = session.status === 'in_progress' || completed;
    return [
      { label: 'Choose Blend', done: true, current: !prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Move Raw', done: prepared, current: !prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Start', done: started, current: prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Complete Bulk', done: completed, current: started && !completed },
    ];
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [blendItemsRes, sessionsRes, lotsRes, movementsRes, rawItemsRes, warehousesRes] = await Promise.all([
        adminApi.getBlendItems(),
        adminApi.getBlendSessions({}),
        adminApi.getBlendedStockLots({}),
        adminApi.getBlendedStockMovements({}),
        adminApi.getRawItems(),
        adminApi.getWarehouses(),
      ]);
      const nextBlendItems = blendItemsRes.data || [];
      setBlendItems(nextBlendItems);
      setBlendSessions(sessionsRes.data?.items || []);
      setBlendLots(lotsRes.data || []);
      setBlendMovements(movementsRes.data || []);
      setRawItems(rawItemsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setSelectedBlendItemId((prev) => prev || nextBlendItems[0]?.id || '');
    } catch (error) {
      toast.error('Failed to load blending data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecipe = async (blendItemId) => {
    if (!blendItemId) {
      setRecipeForm({ notes: '', inputs: [blankRecipeRow()] });
      return;
    }
    try {
      const response = await adminApi.getBlendItemRecipe(blendItemId);
      const recipe = response.data || {};
      setRecipeForm({
        notes: recipe.notes || '',
        inputs: (recipe.inputs || []).length
          ? recipe.inputs.map((item) => ({
              raw_item_id: item.raw_item_id || '',
              ratio_percent: String(item.ratio_percent ?? ''),
              wastage_percent: String(item.wastage_percent ?? ''),
              notes: item.notes || '',
            }))
          : [blankRecipeRow()],
      });
    } catch (error) {
      setRecipeForm({ notes: '', inputs: [blankRecipeRow()] });
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    loadRecipe(selectedBlendItemId);
  }, [selectedBlendItemId]);

  useEffect(() => {
    setSessionForm((prev) => ({
      ...prev,
      source_warehouse_id: prev.source_warehouse_id || packingWarehouseId,
    }));
  }, [packingWarehouseId]);

  const openCreateItemModal = () => {
    setEditingBlendItem(null);
    setItemForm({ blend_name: '', blend_code: '', notes: '', is_active: true });
    setItemModalOpen(true);
  };

  const openEditItemModal = (item) => {
    setEditingBlendItem(item);
    setItemForm({
      blend_name: item.blend_name || '',
      blend_code: item.blend_code || '',
      notes: item.notes || '',
      is_active: item.is_active !== false,
    });
    setItemModalOpen(true);
  };

  const saveBlendItem = async () => {
    if (!itemForm.blend_name.trim()) {
      toast.error('Blend name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingBlendItem) {
        await adminApi.updateBlendItem(editingBlendItem.id, itemForm);
        toast.success('Blend item updated');
      } else {
        await adminApi.createBlendItem(itemForm);
        toast.success('Blend item created');
      }
      setItemModalOpen(false);
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save blend item');
    } finally {
      setSaving(false);
    }
  };

  const saveRecipe = async () => {
    if (!selectedBlendItemId) {
      toast.error('Select a blend item first');
      return;
    }
    const inputs = recipeForm.inputs
      .filter((item) => item.raw_item_id && item.ratio_percent)
      .map((item) => ({
        raw_item_id: item.raw_item_id,
        ratio_percent: Number(item.ratio_percent),
        wastage_percent: Number(item.wastage_percent || 0),
        notes: item.notes || '',
      }));
    if (!inputs.length) {
      toast.error('Add at least one recipe input');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateBlendItemRecipe(selectedBlendItemId, {
        notes: recipeForm.notes || '',
        inputs,
      });
      toast.success('Blend recipe saved');
      await loadAll();
      await loadRecipe(selectedBlendItemId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save blend recipe');
    } finally {
      setSaving(false);
    }
  };

  const openCreateSessionModal = () => {
    setSessionForm({
      session_name: '',
      blend_item_id: selectedBlendItemId || '',
      source_warehouse_id: packingWarehouseId || '',
      planned_output_kg: '',
      planned_raw_inputs: [blankSessionRawRow()],
      notes: '',
    });
    setSessionModalOpen(true);
  };

  const saveBlendSession = async () => {
    if (!sessionForm.blend_item_id || !sessionForm.planned_output_kg) {
      toast.error('Blend item and planned output are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createBlendSession({
        session_name: sessionForm.session_name,
        blend_item_id: sessionForm.blend_item_id,
        source_warehouse_id: sessionForm.source_warehouse_id || null,
        planned_output_kg: Number(sessionForm.planned_output_kg),
        planned_raw_inputs: sessionForm.planned_raw_inputs
          .filter((item) => item.raw_item_id && item.kg_required)
          .map((item) => ({
            raw_item_id: item.raw_item_id,
            kg_required: Number(item.kg_required),
            notes: item.notes || '',
          })),
        notes: sessionForm.notes || '',
      });
      toast.success('Blend session created');
      setSessionModalOpen(false);
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create blend session');
    } finally {
      setSaving(false);
    }
  };

  const handleSessionAction = async (session, action) => {
    try {
      if (action === 'start') {
        await adminApi.startBlendSession(session.id);
        toast.success('Blend session started');
      } else if (action === 'prepare') {
        const preview = await adminApi.previewBlendSessionStockPreparation(session.id);
        if (!preview.data?.ready) {
          const shortageText = (preview.data?.shortages || [])
            .map((item) => `${item.raw_item_name}: ${item.shortage_kg} kg short`)
            .join('; ');
          toast.error(shortageText || 'Blend stock preparation has shortages');
          return;
        }
        await adminApi.confirmBlendSessionStockPreparation(session.id);
        toast.success('Raw stock moved to Packing');
      } else if (action === 'cancel') {
        await adminApi.cancelBlendSession(session.id, {});
        toast.success('Blend session cancelled');
      } else if (action === 'complete') {
        setSelectedSession(session);
        setCompleteForm({
          actual_output_kg: String(session.planned_output_kg || ''),
          production_loss_kg: String(session.production_loss_kg || ''),
          notes: session.notes || '',
          actual_raw_inputs: (session.planned_raw_inputs || []).length
            ? session.planned_raw_inputs.map((item) => ({
                raw_item_id: item.raw_item_id || '',
                kg_required: String(item.kg_required ?? ''),
                notes: item.notes || '',
              }))
            : [blankSessionRawRow()],
          raw_wastage_inputs: (session.planned_raw_inputs || []).length
            ? session.planned_raw_inputs.map((item) => ({ raw_item_id: item.raw_item_id || '', wastage_kg: '', notes: '' }))
            : [blankBlendWastageRow()],
        });
        setCompleteModalOpen(true);
        return;
      }
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update blend session');
    }
  };

  const completeSession = async () => {
    if (!selectedSession) return;
    setSaving(true);
    try {
      await adminApi.completeBlendSession(selectedSession.id, {
        actual_output_kg: Number(completeForm.actual_output_kg),
        production_loss_kg: Number(completeForm.production_loss_kg || 0),
        notes: completeForm.notes || '',
        actual_raw_inputs: completeForm.actual_raw_inputs
          .filter((item) => item.raw_item_id && item.kg_required)
          .map((item) => ({
            raw_item_id: item.raw_item_id,
            kg_required: Number(item.kg_required),
            notes: item.notes || '',
          })),
        raw_wastage_inputs: completeForm.raw_wastage_inputs
          .filter((item) => item.raw_item_id && item.wastage_kg)
          .map((item) => ({
            raw_item_id: item.raw_item_id,
            wastage_kg: Number(item.wastage_kg),
            notes: item.notes || '',
          })),
      });
      toast.success('Blend session completed');
      setCompleteModalOpen(false);
      setSelectedSession(null);
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to complete blend session');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Bulk Blending</h1>
          <p className="mt-1 text-[#A8A8A8]">Blend raw variants into intermediate bulk stock, then use that stock in packing sessions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#E5E5E5]" onClick={openCreateItemModal}>Create Blend Item</Button>
          <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={openCreateSessionModal}>Create Blend Session</Button>
        </div>
      </div>

      <Tabs defaultValue="items" className="space-y-4">
        <TabsList className="bg-[#F7F1EC]">
          <TabsTrigger value="items">Blend Items</TabsTrigger>
          <TabsTrigger value="sessions">Blend Sessions</TabsTrigger>
          <TabsTrigger value="stock">Bulk Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
            <Card className="border-[#E5E5E5]">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-[#2D2420]">Blend Masters</h2>
                </div>
                <div className="space-y-2">
                  {blendItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedBlendItemId(item.id)}
                      className={`w-full rounded-lg border p-3 text-left ${selectedBlendItemId === item.id ? 'border-[#C97A5E] bg-[#FCF4EF]' : 'border-[#E5E5E5] bg-white'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-[#2D2420]">{item.blend_name}</div>
                          <div className="text-xs text-[#A8A8A8]">{item.blend_code || 'Auto code'}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={item.is_active === false ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
                            {item.is_active === false ? 'Inactive' : 'Active'}
                          </Badge>
                          <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={(event) => { event.stopPropagation(); openEditItemModal(item); }}>
                            Edit
                          </Button>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E5E5E5]">
              <CardContent className="p-4 space-y-4">
                <div>
                  <h2 className="font-semibold text-[#2D2420]">Blend Recipe</h2>
                  <p className="text-sm text-[#A8A8A8] mt-1">
                    {activeBlendItem ? `Recipe for ${activeBlendItem.blend_name}` : 'Select a blend item to maintain its default raw mix.'}
                  </p>
                </div>
                {activeBlendItem ? (
                  <>
                    <div className="space-y-2">
                      {recipeForm.inputs.map((row, index) => (
                        <div key={`recipe-${index}`} className="grid gap-2 md:grid-cols-[1.4fr_0.7fr_0.7fr_1fr_auto]">
                          <Select value={row.raw_item_id || undefined} onValueChange={(value) => setRecipeForm((prev) => ({ ...prev, inputs: prev.inputs.map((item, rowIndex) => rowIndex === index ? { ...item, raw_item_id: value } : item) }))}>
                            <SelectTrigger className="border-[#E5E5E5]">
                              <SelectValue placeholder="Select raw item" />
                            </SelectTrigger>
                            <SelectContent>
                              {rawItems.map((item) => (
                                <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input type="number" value={row.ratio_percent} onChange={(e) => setRecipeForm((prev) => ({ ...prev, inputs: prev.inputs.map((item, rowIndex) => rowIndex === index ? { ...item, ratio_percent: e.target.value } : item) }))} placeholder="Ratio %" className="border-[#E5E5E5]" />
                          <Input type="number" value={row.wastage_percent} onChange={(e) => setRecipeForm((prev) => ({ ...prev, inputs: prev.inputs.map((item, rowIndex) => rowIndex === index ? { ...item, wastage_percent: e.target.value } : item) }))} placeholder="Wastage %" className="border-[#E5E5E5]" />
                          <Input value={row.notes} onChange={(e) => setRecipeForm((prev) => ({ ...prev, inputs: prev.inputs.map((item, rowIndex) => rowIndex === index ? { ...item, notes: e.target.value } : item) }))} placeholder="Notes" className="border-[#E5E5E5]" />
                          <Button type="button" variant="outline" className="border-[#E5E5E5]" onClick={() => setRecipeForm((prev) => ({ ...prev, inputs: prev.inputs.length === 1 ? prev.inputs : prev.inputs.filter((_, rowIndex) => rowIndex !== index) }))}>Remove</Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="border-[#E5E5E5]" onClick={() => setRecipeForm((prev) => ({ ...prev, inputs: [...prev.inputs, blankRecipeRow()] }))}>
                        <Plus className="mr-1 h-4 w-4" />
                        Add Input
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>Recipe Notes</Label>
                      <Textarea value={recipeForm.notes} onChange={(e) => setRecipeForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
                    </div>
                    <div className="flex justify-end">
                      <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={saveRecipe} disabled={saving}>
                        <Save className="mr-1 h-4 w-4" />
                        Save Recipe
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border border-dashed border-[#E5E5E5] p-6 text-sm text-[#A8A8A8]">No blend item selected.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sessions" className="space-y-4">
          <Card className="border-[#E5E5E5]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-[#E5E5E5] bg-[#FAFAF9]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#5C4033]">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#5C4033]">Blend Item</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#5C4033]">Planned / Actual</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#5C4033]">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#5C4033]">Created</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-[#5C4033]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {blendSessions.map((session) => (
                      <tr key={session.id}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#2D2420]">{session.session_ref}</div>
                          <div className="text-sm text-[#A8A8A8]">{session.batch_code || session.session_name || '-'}</div>
                          <div className="mt-3 flex flex-wrap gap-1">
                            {getBlendSessionSteps(session).map((step, index) => (
                              <span
                                key={`${session.id}-${step.label}`}
                                className={`rounded-full px-2 py-1 text-[11px] font-medium ${step.done ? 'bg-emerald-100 text-emerald-700' : step.current ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
                              >
                                {index + 1}. {step.label}
                              </span>
                            ))}
                          </div>
                          {session.stock_preparation_status === 'prepared' ? (
                            <div className="mt-2 text-xs text-emerald-700">Raw stock moved to Packing</div>
                          ) : ['draft', 'planned'].includes(session.status) ? (
                            <div className="mt-2 text-xs text-amber-700">Next: move recipe raw stock from Main to Packing</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-[#2D2420]">{session.blend_item_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-[#2D2420]">
                          <div>Planned: {session.planned_output_kg} kg</div>
                          <div>Actual: {session.actual_output_kg || 0} kg</div>
                        </td>
                        <td className="px-4 py-3"><Badge className="bg-slate-100 text-slate-700">{session.status}</Badge></td>
                        <td className="px-4 py-3 text-[#A8A8A8]">{formatDateTime(session.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            {['draft', 'planned'].includes(session.status) ? (
                              <>
                                {session.stock_preparation_status !== 'prepared' ? (
                                  <Button type="button" size="sm" variant="outline" className="border-amber-200 text-amber-700" onClick={() => handleSessionAction(session, 'prepare')}>
                                    <Truck className="mr-1 h-4 w-4" />
                                    Move Raw
                                  </Button>
                                ) : null}
                                <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={() => handleSessionAction(session, 'start')} disabled={session.stock_preparation_status !== 'prepared'}>
                                  <Play className="mr-1 h-4 w-4" />
                                  Start
                                </Button>
                              </>
                            ) : null}
                            {['draft', 'planned', 'in_progress'].includes(session.status) ? (
                              <Button type="button" size="sm" className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={() => handleSessionAction(session, 'complete')}>
                                <CheckCircle2 className="mr-1 h-4 w-4" />
                                Complete
                              </Button>
                            ) : null}
                            {session.status !== 'completed' && session.status !== 'cancelled' ? (
                              <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={() => handleSessionAction(session, 'cancel')}>
                                <Ban className="mr-1 h-4 w-4" />
                                Cancel
                              </Button>
                            ) : null}
                            {session.status === 'completed' ? (
                              <Button type="button" size="sm" className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={() => { window.location.href = '/warehouse/packing-sessions'; }}>
                                Create Packing Session
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stock" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-[#E5E5E5]">
              <CardContent className="p-4 space-y-3">
                <h2 className="font-semibold text-[#2D2420]">Bulk Blend Stock</h2>
                <div className="space-y-2">
                  {blendLots.map((lot) => (
                    <div key={lot.id} className="rounded-lg border border-[#E5E5E5] p-3">
                      <div className="font-medium text-[#2D2420]">{lot.blend_item_name}</div>
                      <div className="text-sm text-[#A8A8A8]">{lot.warehouse_name} • {lot.stock_ref_no}</div>
                      <div className="mt-1 text-sm text-[#5C4033]">{lot.kg_total} kg available</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-[#E5E5E5]">
              <CardContent className="p-4 space-y-3">
                <h2 className="font-semibold text-[#2D2420]">Blend Movements</h2>
                <div className="space-y-2">
                  {blendMovements.slice(0, 25).map((movement) => (
                    <div key={movement.id} className="rounded-lg border border-[#E5E5E5] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-[#2D2420]">{movement.blend_item_name}</div>
                          <div className="text-sm text-[#A8A8A8]">{movement.warehouse_name} • {movement.stock_ref_no || movement.ref_id}</div>
                        </div>
                        <Badge className={movement.type === 'produce' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {movement.type}
                        </Badge>
                      </div>
                      <div className="mt-1 text-sm text-[#5C4033]">{movement.kg} kg</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={itemModalOpen} onOpenChange={setItemModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingBlendItem ? 'Edit Blend Item' : 'Create Blend Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Blend Name</Label>
              <Input value={itemForm.blend_name} onChange={(e) => setItemForm((prev) => ({ ...prev, blend_name: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div className="space-y-2">
              <Label>Blend Code</Label>
              <Input value={itemForm.blend_code} onChange={(e) => setItemForm((prev) => ({ ...prev, blend_code: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={itemForm.notes} onChange={(e) => setItemForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemModalOpen(false)}>Cancel</Button>
            <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={saveBlendItem} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sessionModalOpen} onOpenChange={setSessionModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Blend Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Session Name</Label>
                <Input value={sessionForm.session_name} onChange={(e) => setSessionForm((prev) => ({ ...prev, session_name: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div className="space-y-2">
                <Label>Bulk Blend Item</Label>
                <Select value={sessionForm.blend_item_id || undefined} onValueChange={(value) => setSessionForm((prev) => ({ ...prev, blend_item_id: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select bulk blend item" />
                  </SelectTrigger>
                  <SelectContent>
                    {blendItems.filter((item) => item.is_active !== false).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.blend_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source Warehouse</Label>
                <Select value={sessionForm.source_warehouse_id || undefined} onValueChange={(value) => setSessionForm((prev) => ({ ...prev, source_warehouse_id: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Planned Output (kg)</Label>
                <Input type="number" value={sessionForm.planned_output_kg} onChange={(e) => setSessionForm((prev) => ({ ...prev, planned_output_kg: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Planned Raw Inputs</Label>
                <Button type="button" variant="outline" className="border-[#E5E5E5]" onClick={() => setSessionForm((prev) => ({ ...prev, planned_raw_inputs: [...prev.planned_raw_inputs, blankSessionRawRow()] }))}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Raw
                </Button>
              </div>
              {sessionForm.planned_raw_inputs.map((row, index) => (
                <div key={`session-raw-${index}`} className="grid gap-2 md:grid-cols-[1.4fr_0.8fr_1fr]">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => setSessionForm((prev) => ({ ...prev, planned_raw_inputs: prev.planned_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, raw_item_id: value } : item) }))}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.kg_required} onChange={(e) => setSessionForm((prev) => ({ ...prev, planned_raw_inputs: prev.planned_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, kg_required: e.target.value } : item) }))} placeholder="KG" className="border-[#E5E5E5]" />
                  <Input value={row.notes} onChange={(e) => setSessionForm((prev) => ({ ...prev, planned_raw_inputs: prev.planned_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, notes: e.target.value } : item) }))} placeholder="Notes" className="border-[#E5E5E5]" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionModalOpen(false)}>Cancel</Button>
            <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={saveBlendSession} disabled={saving}>{saving ? 'Saving...' : 'Create Session'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Complete Blend Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedSession ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-sm font-medium text-emerald-900">Prepared Raw Movement</div>
                <p className="mt-1 text-xs text-emerald-800">
                  {selectedSession.stock_preparation_status === 'prepared'
                    ? `Moved from ${selectedSession.prep_source_warehouse_name || 'Main Warehouse'} to ${selectedSession.source_warehouse_name || 'Packing'}.`
                    : 'Raw stock has not been prepared yet.'}
                </p>
                <div className="mt-3 space-y-1">
                  {(selectedSession.raw_transfer_snapshot || []).length ? selectedSession.raw_transfer_snapshot.map((item, index) => (
                    <div key={`blend-raw-transfer-${index}`} className="text-xs text-emerald-900">
                      {item.raw_item_name || item.raw_item_id}: {item.kg_moved} kg
                    </div>
                  )) : <div className="text-xs text-emerald-800">No transfer recorded.</div>}
                </div>
              </div>
            ) : null}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Actual Output (kg)</Label>
                <Input type="number" value={completeForm.actual_output_kg} onChange={(e) => setCompleteForm((prev) => ({ ...prev, actual_output_kg: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div className="space-y-2">
                <Label>Production Loss (kg)</Label>
                <Input type="number" value={completeForm.production_loss_kg} onChange={(e) => setCompleteForm((prev) => ({ ...prev, production_loss_kg: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
            </div>
            <div className="space-y-3">
              <Label>Actual Raw Inputs</Label>
              {completeForm.actual_raw_inputs.map((row, index) => (
                <div key={`complete-raw-${index}`} className="grid gap-2 md:grid-cols-[1.4fr_0.8fr_1fr]">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => setCompleteForm((prev) => ({ ...prev, actual_raw_inputs: prev.actual_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, raw_item_id: value } : item) }))}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.kg_required} onChange={(e) => setCompleteForm((prev) => ({ ...prev, actual_raw_inputs: prev.actual_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, kg_required: e.target.value } : item) }))} placeholder="KG" className="border-[#E5E5E5]" />
                  <Input value={row.notes} onChange={(e) => setCompleteForm((prev) => ({ ...prev, actual_raw_inputs: prev.actual_raw_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, notes: e.target.value } : item) }))} placeholder="Notes" className="border-[#E5E5E5]" />
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Label>Raw Wastage</Label>
              {completeForm.raw_wastage_inputs.map((row, index) => (
                <div key={`blend-wastage-${index}`} className="grid gap-2 md:grid-cols-[1.4fr_0.8fr_1fr]">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => setCompleteForm((prev) => ({ ...prev, raw_wastage_inputs: prev.raw_wastage_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, raw_item_id: value } : item) }))}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.wastage_kg} onChange={(e) => setCompleteForm((prev) => ({ ...prev, raw_wastage_inputs: prev.raw_wastage_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, wastage_kg: e.target.value } : item) }))} placeholder="Wastage KG" className="border-[#E5E5E5]" />
                  <Input value={row.notes} onChange={(e) => setCompleteForm((prev) => ({ ...prev, raw_wastage_inputs: prev.raw_wastage_inputs.map((item, rowIndex) => rowIndex === index ? { ...item, notes: e.target.value } : item) }))} placeholder="Reason / note" className="border-[#E5E5E5]" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>Cancel</Button>
            <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={completeSession} disabled={saving}>{saving ? 'Posting...' : 'Complete Session'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
